import { useQuery } from '@apollo/client';
import { AutocompleteProps, AutocompleteValue } from '@mui/material';
import { partition, propEq, reject } from 'ramda';
import { HTMLAttributes, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { AstroAvailabilityStatus, AstroResponsibleUserItem } from '@/generated/gql/graphql';
import { isString } from '~/util/string';

import { RESPONSIBLE_USERS_QUERY } from './QueryResponsibleUsers.gql';
import { UserOption } from './UserOption/UserOption';

export enum UserHitId {
  ME = 'ME',
  EMPTY = 'EMPTY',
}

type CreateStaticHitProps = {
  value: string;
} & AstroResponsibleUserItem;

export const createStaticHit = ({ id, value, ...userProps }: CreateStaticHitProps) => ({
  ...userProps,
  id,
  firstName: value, // use first name for displaying sentinel
  lastName: '',
});

const createEmptyHit = (label: string): AstroResponsibleUserItem => ({
  id: UserHitId.EMPTY,
  primarySourceReference: UserHitId.EMPTY,
  firstName: label,
  lastName: '',
  availabilityStatus: AstroAvailabilityStatus.HIGH,
  demandCount: 0,
  isRecommended: false,
  profilePicture: null,
});

enum GroupOptions {
  RECOMMENDED = 'recommended',
  OTHERS = 'others',
}

type AllAutocompleteProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean = true,
> = AutocompleteProps<AstroResponsibleUserItem, Multiple, DisableClearable, FreeSolo>;

export type AstronautAutocompleteValue<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean = true,
> = AutocompleteValue<AstroResponsibleUserItem, Multiple, DisableClearable, FreeSolo>;

export interface AstronautUserAutocompleteProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean = true,
> extends Pick<AllAutocompleteProps<Multiple, DisableClearable, FreeSolo>, 'size' | 'fullWidth'> {
  value?: Multiple extends true ? string[] : AstronautAutocompleteValue;
  multiple?: Multiple;
  capabilityIds?: string[];
  commodityGroupIds?: string[];
  hasEmptyOption?: boolean;
  translationPrefix?: string;
  onChange?: (value: Multiple extends true ? string[] | undefined : AstronautAutocompleteValue) => void;
}

const matchWithPrimarySourceReference = (
  value?: AstronautAutocompleteValue,
  primarySourceReference?: string,
): boolean => {
  if (!value) {
    return false;
  }
  if (isString(value)) {
    return value === primarySourceReference;
  }
  return value.primarySourceReference === primarySourceReference;
};

const addAssignMeOption = (assignMeOption: AstroResponsibleUserItem, allOptions: AstroResponsibleUserItem[]) => {
  const usersWithoutMe = reject(propEq(assignMeOption.primarySourceReference, 'primarySourceReference'), allOptions);
  const [recommendedUsers, otherUsers] = partition(propEq(true, 'isRecommended'), usersWithoutMe);
  if (assignMeOption.isRecommended) {
    return [assignMeOption].concat(recommendedUsers).concat(otherUsers);
  }
  return recommendedUsers.concat([assignMeOption]).concat(otherUsers);
};

export const useAstronautUserAutocomplete = <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean = true,
>({
  value: valueProp,
  multiple,
  capabilityIds = [],
  commodityGroupIds = [],
  hasEmptyOption,
  translationPrefix,
  onChange: onChangeFunc,
}: AstronautUserAutocompleteProps<Multiple, DisableClearable, FreeSolo>) => {
  const { data, loading } = useQuery(RESPONSIBLE_USERS_QUERY, {
    variables: {
      capabilityIds,
      commodityGroupIds: commodityGroupIds.filter(Boolean),
    },
  });
  const { t } = useTranslation(undefined, { keyPrefix: translationPrefix ?? 'AstronautAutocomplete' });

  const { user } = useAuth();

  const currentUserId = user?.profile.sub;

  const fetchedOptions = useMemo(() => data?.astronaut.responsibleUsers ?? [], [data]);

  const getOptionLabel: NonNullable<AllAutocompleteProps['getOptionLabel']> = useCallback(
    (option: AstronautAutocompleteValue) => {
      const result = typeof option !== 'string' ? `${option?.firstName ?? ''} ${option?.lastName ?? ''}`.trim() : '';
      return result;
    },
    [],
  );

  const renderOption: NonNullable<AllAutocompleteProps['renderOption']> = useCallback(
    (params: HTMLAttributes<HTMLLIElement>, option: AstroResponsibleUserItem) => {
      if (option.id === UserHitId.EMPTY) {
        return (
          <li {...params} key={option.id}>
            {option.firstName}
          </li>
        );
      }
      return <UserOption {...params} data={option} key={option.id} />;
    },
    [],
  );

  const groupBy: NonNullable<AllAutocompleteProps['groupBy']> = useCallback(
    (option: AstroResponsibleUserItem) => (option.isRecommended ? GroupOptions.RECOMMENDED : GroupOptions.OTHERS),
    [],
  );

  const multipleSelection = useMemo(
    () => (multiple ? ((valueProp as string[] | undefined) ?? []) : []),
    [multiple, valueProp],
  );

  const multipleOptions: AstroResponsibleUserItem[] = useMemo(() => {
    const result: AstroResponsibleUserItem[] = [];
    const meOption = fetchedOptions.find((option) => option.primarySourceReference === currentUserId);
    if (meOption) {
      result.push(
        createStaticHit({ ...meOption, id: UserHitId.ME, value: t(UserHitId.ME) }) as AstroResponsibleUserItem,
      );
    }
    if (hasEmptyOption) {
      result.push(createEmptyHit(t(UserHitId.EMPTY)));
    }
    const rest = fetchedOptions.filter((option) => option.primarySourceReference !== currentUserId);
    return result.concat(rest);
  }, [fetchedOptions, currentUserId, hasEmptyOption, t]);

  const multipleValue = useMemo(
    () =>
      multipleSelection.map(
        (reference) =>
          multipleOptions.find((option) => option.primarySourceReference === reference) ??
          createEmptyHit(reference === UserHitId.EMPTY ? t(UserHitId.EMPTY) : reference),
      ),
    [multipleSelection, multipleOptions, t],
  );

  const multipleOnChange = useCallback(
    (_event: unknown, value: AstroResponsibleUserItem[]) => {
      const next = value.map((item) => (isString(item) ? item : item.primarySourceReference));
      onChangeFunc?.((next.length ? next : undefined) as never);
    },
    [onChangeFunc],
  );

  const isCurrentUserSelected = matchWithPrimarySourceReference(valueProp as AstronautAutocompleteValue, currentUserId);

  const singleOptions: AstroResponsibleUserItem[] = useMemo(() => {
    let assignMeOption: AstroResponsibleUserItem | undefined = undefined;
    const currentUserArrIndex = fetchedOptions.findIndex((user) => user.primarySourceReference === currentUserId);
    if (currentUserArrIndex > -1 && !isCurrentUserSelected) {
      assignMeOption = createStaticHit({
        ...fetchedOptions[currentUserArrIndex],
        id: UserHitId.ME,
        value: t(UserHitId.ME),
      });
    }
    if (assignMeOption) {
      return addAssignMeOption(assignMeOption, fetchedOptions);
    }
    return fetchedOptions;
  }, [currentUserId, fetchedOptions, isCurrentUserSelected, t]);

  const singleValue = useMemo(() => {
    return (
      fetchedOptions.find((option) => typeof valueProp === 'string' && option.primarySourceReference === valueProp) ??
      null
    );
  }, [fetchedOptions, valueProp]);

  const singleOnChange: NonNullable<AllAutocompleteProps['onChange']> = useCallback(
    (_event, value) => {
      onChangeFunc?.((typeof value === 'string' ? value : (value?.primarySourceReference ?? null)) as never);
    },
    [onChangeFunc],
  );

  const autocompleteProps = {
    multiple,
    getOptionLabel,
    renderOption,
    groupBy,
    value: multiple ? multipleValue : singleValue,
    options: multiple ? multipleOptions : singleOptions,
    onChange: multiple ? multipleOnChange : singleOnChange,
  } as unknown as Partial<AutocompleteProps<AstroResponsibleUserItem, false, false, true>>;

  return { loading, ...autocompleteProps };
};
